<style lang="scss">
    .calendar-content {
        min-height: 450px;
        height: auto;
        width: 550px;
        background-color: white !important;
        position: absolute;
        left: -500px;
        top: -3px;
        border-radius: 5px;
        color: black;
        box-shadow: 2px 2px 10px black;
        overflow-y: auto;
        overflow-x: hidden;
        max-height: 850px;
        padding: 15px 5px 15px 15px;
    }

    .calendar-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 5;
    }

    @media screen and (max-width: 768px) {
        .calendar-content {
            min-height: 85vh;
            height: auto;
            width: 85vw !important;
            background-color: white !important;
            position: absolute;
            left: -67vw;
            border-radius: 5px;
            color: black;
            box-shadow: 2px 2px 10px black;
            overflow-y: auto;
            overflow-x: hidden;
            max-height: 850px;
            padding: 15px 5px 15px 15px;
        }
    }

</style>

<template>
    <div>
        <div class="calendar-backdrop" ref="calendar-backdrop" v-show="shown" @click.prevent="handleFocusOut()"></div>
        <Popper class="text-xs" ref="popper" :show="shown" :z-index="50" @open:popper="initPopper">
            <div @click="toggleCalendar()" v-tooltip data-bs-toggle="tooltip" data-bs-placement="bottom" :data-bs-title="$t('navbar.calendar')" data-bs-trigger="hover" >
                <span class="navlink">
                    <i class="fa-solid fa-calendar-days"></i>
                </span>
            </div>
            <template #content>
                <div ref="calendarFocus" tabindex="0">
                    <div class="arrow"></div>
                    <div class="calendar-content bg-general">
                        <div class="row">
                            <div class="btn-group btn-group-sm">
                                <button v-if="this.$store.permissionStore?.canAccess('canMakeTestCall')" class="btn" @click.prevent="phoneCallTest()"
                                    v-tooltip data-bs-toggle="tooltip" data-bs-placement="bottom" :data-bs-title="$t('calendar.testCall')" data-bs-trigger="hover" 
                                >
                                    <i class="fa-solid fa-phone text-black"></i>
                                </button>
                                <button class="btn btn-light" v-for="spec in SpecializationEnum" :class="{'selected': spec.id === selectedSpecialization.id}"
                                        @click.prevent="switchSpecialization(spec)">
                                    {{ spec.name }}
                                </button>
                                <button v-if="this.$store.permissionStore?.canAccess('canEditCalendar')" class="btn" @click.prevent="openCalendar()"
                                    v-tooltip data-bs-toggle="tooltip" data-bs-placement="bottom" :data-bs-title="$t('calendar.exceptions')" data-bs-trigger="hover" 
                                >
                                    <i class="fa-solid fa-pen-clip text-black"></i>
                                </button>
                            </div>
                        </div>
                        <div class="mt-2 row">
                            <VueDatePicker v-model="selectedDate" :inline="true" :key="selectedSpecialization.id"
                                           multi-calendars @update:model-value="updateCalendar" :markers="markers"
                                           :enable-time-picker="false" :day-class="getDayStyle" auto-apply :month-change-on-scroll="false">
                                <template #marker="{ marker, day, date }">
                                    <span :style="{'background-color': marker.color}" class="custom-marker"></span>
                                </template>
                            </VueDatePicker>
                        </div>
                        <div class="mt-2 row">
                            <ul>
                                <li v-for="tech in getTechsForSpecializations" class="flex flex-row align-items-center">
                                    <div class="w-3 h-3 rounded-full" :style="{backgroundColor: tech.color}"></div>
                                    <div class="ms-1" :class="{'fw-bold':tech.id === selectedTechnician.id }" @click.prevent="setSelectedTech(tech)">
                                        {{ this.$general.getFullName(tech) }}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </template>
        </Popper>
        <ToastComponent ref="toast"></ToastComponent>
    </div>
    <ConfirmDialog ref="confirmDialog" :is-save-valid="true"></ConfirmDialog>
</template>

<script>
    import Popper from "vue3-popper";
    import ToastComponent from "@/components/_shared/ToastComponent.vue";
    import VueDatePicker from '@vuepic/vue-datepicker';
    import ConfirmDialog from "@/components/_shared/ConfirmDialog.vue";

    export default {
        name: "CalendarPopup",
        components: {
            ConfirmDialog,
            VueDatePicker,
            Popper,
            ToastComponent,
        },
        props: {},
        data() {
            return {
                shown: false,
                timeSlots: [],
                selectedDate: null,
                selectedSpecialization: { id: 1, },
                selectedTechnician: {},
                technicians: [],
            }
        },
        computed: {
            getTechsForSpecializations() {
                return this.$general.orderByCustom(this.technicians.filter(tech => tech.specializations?.some(spec => spec.id === this.selectedSpecialization.id)), 'full_name', true);
            },
            filteredTimeSlots() {
                return this.timeSlots.filter(timeSlot =>
                    this.$general.isDateBetween(timeSlot.date_start,
                        this.$general.getFromDate(undefined, "subtract", 1, "year"),
                        this.$general.getFromDate(undefined, "add", 1, "year")
                    ) &&
                    timeSlot.specialization_id === this.selectedSpecialization.id
                );
            },
            filteredExceptions() {
                return this.filteredTimeSlots.filter(timeSlot => timeSlot.is_exception === true );
            },
            SpecializationEnum() {
                return this.$store.enumsStore.Specializations;
            },
            markers() {
                const markers = [];

                for (const exception of this.filteredExceptions) {
                    const user = this.technicians.find(
                        tech => tech.id === exception.user_id
                    );

                    let current = this.$dayjs(exception.date_start).startOf('day');
                    const end = this.$dayjs(exception.date_end).startOf('day');

                    while (
                        current.isBefore(end) ||
                        current.isSame(end)
                        ) {
                        markers.push({
                            date: current.toDate(),
                            type: 'dot',
                            color: user?.color,
                            tooltip: [{
                                text: `${this.$general.getFullName(user)} De: ${this.$general.formatDate(exception.date_start, 'YYYY-MM-DD HH:mm')} À: ${this.$general.formatDate(exception.date_end, 'YYYY-MM-DD HH:mm')}`,
                                color: user?.color
                            }]
                        });

                        current = current.add(1, 'day');
                    }
                }

                return markers;
            },
        },
        mounted() {
            if (this.$store.authUserStore.fullUserDetails.specializations?.length === 1 &&
                this.$store.authUserStore.userHasSpecialization(this.SpecializationEnum.SCADA.id)) {
                this.specialization = this.SpecializationEnum.SCADA;
            } else {
                this.specialization = this.SpecializationEnum.PLC;
            }
        },
        methods: {
            async initPopper() {
                await this.getAllTimeSlots();
                await this.getAnswerers();
                this.injectUserColors();
            },
            async getAllTimeSlots() {
                await this.$axios.get('/support/getAllTimeSlots').then((response) => {
                    this.timeSlots = response;
                });
            },
            async getAnswerers() {
                await this.$axios.get('/user/getAnswerers').then((response) => {
                    this.technicians = response;
                });
            },
            toggleCalendar() {
                this.shown = !this.shown;
            },
            handleFocusOut() {
                setTimeout(() => {
                    this.toggleCalendar();
                }, 100);
            },
            switchSpecialization(spec) {
                this.selectedTechnician = {};
                this.markers = [];
                this.selectedSpecialization = spec;
            },
            setSelectedTech(tech = {}) {
                if (!this.$store.permissionStore?.canAccess('canEditCalendar')) { return; }
                this.selectedTechnician = this.selectedTechnician?.id === tech?.id ? {} : tech;
            },
            getDayStyle(date) {
                for (const slot of this.filteredTimeSlots) {
                    if (this.$general.isDateSameOrAfter(date, this.$dayjs(this.$general.formatDate(slot.date_start, 'YYYY-MM-DD'))) &&
                        this.$general.isDateBefore(date, this.$dayjs(this.$general.formatDate(slot.date_end, 'YYYY-MM-DD')))
                    ) {
                        const user = this.technicians.find((tech) => tech.id === slot.user_id);
                        if (user === undefined || user === null) { return ''; }

                        return `highlight-${user.color.replace('#','')}`;
                    }
                }
                return '';
            },
            injectUserColors() {
                let style = document.createElement('style');
                style.type = 'text/css';
                let css = '';

                this.technicians.reduce((acc, tech) => {
                    const colors = tech.color;
                    if (!acc.includes(colors)) {
                        acc.push(colors);
                    }
                    return acc;
                }, []).forEach(color => {
                    const className = `highlight-${color?.replace('#', '')}`;
                    css += `
                    .${className} {
                        background-color: ${color};
                        color: white;
                        border-radius: 50%;
                        border-width: 2px;
                        }
                        `;
                });
                style.appendChild(document.createTextNode(css));
                document.head.appendChild(style);
            },
            openCalendar() {
                if (!this.$store.permissionStore?.canAccess('canEditCalendar')) { return; }
                this.$router.push({name: 'Calendar'});
                this.toggleCalendar();
            },
            async updateCalendar() {
                if (!(this.selectedTechnician.id > 0) || !this.$general.isValidDate(this.selectedDate) || !this.$store.permissionStore?.canAccess('canEditCalendar')) { return; }

                let startOfWeek = this.$general.formatDate(this.$general.getStartOfIsoWeek(this.selectedDate), 'YYYY-MM-DD');

                const supportCalendar = this.timeSlots.find(
                    (timeSlot) => this.$general.isDateSame(this.$general.formatDate(timeSlot.date_start, 'YYYY-MM-DD'), startOfWeek) &&
                        timeSlot.specialization_id === this.selectedSpecialization.id
                );
                if (supportCalendar?.id > 0) {
                    const confirm = await this.$refs.confirmDialog.show({
                        title: 'Calendrier',
                        message: `${this.$t('calendar.confirmDialog')} ${this.$general.getFullName(this.technicians.find(tech => tech.id === supportCalendar?.user_id))} ${this.$t('calendar.by')} ${this.$general.getFullName(this.selectedTechnician)} ${this.$t('calendar.weekOf')} ${startOfWeek}`,
                        okButton: this.$t('calendar.okButton'),
                        denyButton: this.$t('general.denyButton'),
                        showCancel: false
                    });

                    if (confirm === true) {
                        this.$axios.post('/support/replaceSupportCalendar', {
                            support_calendar_id: supportCalendar.id,
                            user_id: this.selectedTechnician.id,
                        }, 'string').then(async (response) => {
                            await this.getAllTimeSlots();
                            this.setSelectedTech();

                            const message = (response === "OK") ? this.$t('general.toastSave') : this.$t('general.toastError');
                            const type = (response === "OK") ? 'save' : 'error';
                            this.selectedTechnician = {};
                            this.$refs.toast.setEverything('Calendrier', '', message, type);
                            this.$refs.toast.show();
                        });
                    } else return confirm === "deny";

                } else {
                    const confirm = await this.$refs.confirmDialog.show({
                        title: 'Calendrier',
                        message: `${this.$t('calendar.confirmAddDialog')} ${this.$general.getFullName(this.selectedTechnician)} ${this.$t('calendar.weekOf')} ${startOfWeek}`,
                        okButton: this.$t('calendar.addButton'),
                        denyButton: this.$t('general.denyButton'),
                        showCancel: false
                    });

                    if (confirm === true) {
                        const timeSlot =  {
                            timezone: this.selectedTechnician.timezone_offset,
                            date_start: this.$general.getStartOfIsoWeek(this.selectedDate), 
                            date_end: this.$general.getStartOfIsoWeek(this.$general.getFromDate(this.selectedDate, "add", 1, "week")),
                            user_id: this.selectedTechnician.id,
                            specialization_id: this.selectedSpecialization.id,
                            is_exception: false,
                            exception_sent_status_id: 0,
                        };
                        await this.$axios.post('/support/addTimeSlot', timeSlot, 'number').then(async (response) => {
                            await this.getAllTimeSlots();
                            this.setSelectedTech();
                            const message = (response > 0) ? this.$t('general.toastSave') : this.$t('general.toastError');
                            const type = (response > 0) ? 'save' : 'error';
                            this.$refs.toast.setEverything('Calendrier', '', message, type);
                            this.$refs.toast.show();
                        });
                    } else return confirm === "deny";
                }
            },
            phoneCallTest() {
                this.$axios.post('/support/phoneCallTest', {specialization_id: this.selectedSpecialization.id}, 'string').then((response) => {
                    this.$refs.toast.setEverything('Calendrier', '', $t('smsSent'), "save");
                    this.$refs.toast.show();
                });
            }
        },
        watch: {},
    }
</script>